import { get } from 'lodash';

/**
 * Gets localized label for a category
 * @param {string} category - Category key
 * @param {Object} i18n - i18n instance
 * @param {Object} searchConfig - Search configuration
 * @returns {string} Localized label
 */
export function getCategoryLabel(category, i18n, searchConfig = {}) {
  const categoryConfig = get(searchConfig, ['categories', category], {});

  if (categoryConfig.label) {
    return categoryConfig.label;
  }

  try {
    const i18nCategories = i18n.te('Core.MultiSearch.categories') && i18n.t('Core.MultiSearch.categories');
    if (i18nCategories[category]) {
      return i18nCategories[category];
    }
  } catch (error) {
    console.warn('i18n error in getCategoryLabel:', error);
  }

  return category;
}

/**
 * Gets localized label for a the condition
 * @param {string} condition - Condition key ('is' or 'isnot')
 * @param {Object} i18n - i18n instance
 * @returns {string} Localized label
 */
export function getConditionLabel(condition, i18n) {
  const conditionMap = {
    is: 'Core.MultiSearch.conditions.is',
    isnot: 'Core.MultiSearch.conditions.isNot',
  };

  const i18nKey = conditionMap[condition];

  if (i18n && i18n.te && i18n.te(i18nKey)) {
    return i18n.t(i18nKey);
  }

  return condition === 'isnot' ? 'is not' : 'is';
}

/**
 * Gets human-readable display value for a rule
 * @param {Object} rule - Rule object with flat structure
 * @param {Object} i18n - i18n instance
 * @param {Object} searchConfig - Search configuration
 * @returns {string} Human-readable display value
 */
export function getRuleDisplayValue(rule, i18n, searchConfig = {}) {
  const { field } = rule;
  const fieldConfig = get(searchConfig, `categories.${field}`, {});

  if (fieldConfig.type === 'keyValue') {
    const key = get(rule, 'key', '');
    const value = get(rule, 'value', '');
    const displayValue = get(rule, 'text', value);
    if (!key) {
      return displayValue;
    }
    return `${key} = ${displayValue}`;
  }

  if (rule.text) {
    return rule.text;
  }

  const value = get(rule, 'value', '');

  if (fieldConfig.type === 'select' && fieldConfig.options) {
    const options = fieldConfig.options || [];
    const option = options.find((opt) => opt.value === value);
    if (option) {
      return option.text || option.label || value;
    }
  }

  return value;
}

/**
 * Gets complete human-readable rule description
 * @param {Object} rule - Rule object
 * @param {Object} i18n - i18n instance
 * @param {Object} searchConfig - Search configuration
 * @returns {Object} Object with categoryLabel, conditionLabel, and valueLabel
 */
export function getRuleDescription(rule, i18n, searchConfig = {}) {
  const fieldLabel = getCategoryLabel(rule.field, i18n, searchConfig);
  const conditionLabel = getConditionLabel(rule.condition, i18n);
  const valueLabel = getRuleDisplayValue(rule, i18n, searchConfig);

  return {
    categoryLabel: fieldLabel,
    conditionLabel: conditionLabel,
    valueLabel,
  };
}

/**
 * Gets all available categories with their labels
 * @param {Object} searchConfig - Search configuration
 * @param {Object} i18n - i18n instance
 * @returns {Array} Array of category objects with value and text
 */
export function getCategoryOptions(searchConfig, i18n) {
  const categories = get(searchConfig, 'categories', {});

  return Object.keys(categories).map((category) => ({
    value: category,
    text: getCategoryLabel(category, i18n, searchConfig),
  }));
}
